/**
 * Configuration of the form validation
 * @module embed/form_validation
 */

import formValidation from '@/../vendor/formvalidation-1.3.0/dist/es6/core/Core';
import Declarative from '@/../vendor/formvalidation-1.3.0/dist/es6/plugins/Declarative';
import Message from '@/../vendor/formvalidation-1.3.0/dist/es6/plugins/Message';
import Bootstrap from '@/../vendor/formvalidation-1.3.0/dist/es6/plugins/Bootstrap';
import Trigger from './form_validation/plugins/Trigger';
import en_US from '@/../vendor/formvalidation-1.3.0/dist/es6/locales/en_US';
import L10n from '@/../vendor/formvalidation-1.3.0/dist/es6/plugins/L10n';
import Aria from '@/../vendor/formvalidation-1.3.0/dist/es6/plugins/Aria';
import Excluded from '@/../vendor/formvalidation-1.3.0/dist/es6/plugins/Excluded';
import messages from './form_validation/messages';
import zipCode from '@/../vendor/formvalidation-1.3.0/dist/es6/validators/zipCode';

// Marks the form on which the validations will be set up
const SELECTOR = '.js-bcs-form';
// Marks a custom boundary into which to add the errors
const SELECTOR_ERROR_BOUNDARY = '.js-error-boundary';
// Attribute for marking that the field should not be validated
export const ATTR_NO_VALIDATION = 'data-fv-novalidate';
// Formvalidation excludes invisible elements by default
// This attribute forces their validation, unless the element
// is disabled (in which case it won't be sent anyway)
export const ATTR_FORCE_VALIDATION = 'data-fv-force-validation-unless-disabled';
// Attribute that masks the error message
export const ATTR_NO_MESSAGE = 'data-fv-nomessage';

export default function setupFormValidation(el) {
  const form = el.querySelector(SELECTOR);
  form.setAttribute('novalidate', '');

  // Configure where error messages will be displayed
  const messagePlugin = new Message({
    container(field, el) {
      if (el.classList.contains('js-error-boundary__field')) {
        return el.closest(SELECTOR_ERROR_BOUNDARY);
      }
      return el.closest('fieldset,.form-group');
    }
  });

  // Use HTML5 attributes (required, min, max...)
  // for validations
  const declarativePlugin = new Declarative({
    html5Input: true
  });

  // Actually configure the validator
  const validator = formValidation(form, {
    locale: 'en_US',
    localization: en_US,
    plugins: {
      declarative: declarativePlugin,
      bootstrap: new Bootstrap({
        // As the Message plugin uses custom containers
        // we don't use the Bootstrap's plugin default message container
        defaultMessageContainer: false
      }),
      message: messagePlugin,
      // Validate the form on change
      trigger: new Trigger({
        event: 'change'
      }),
      // Render `aria-invalid`...
      aria: new Aria(),
      l10n: new L10n(messages),
      // Tweak which elements are excluded from validations
      excluded: new Excluded({
        excluded(field, element, elements) {
          return (
            (Excluded.defaultIgnore(field, element, elements) ||
              element.hasAttribute(ATTR_NO_VALIDATION)) &&
            !(element.hasAttribute(ATTR_FORCE_VALIDATION) && !element.disabled)
          );
        }
      })
    }
  })
    // Add the zipCode validation
    .registerValidator('zipCode', zipCode)
    // Trim whitespace before validating
    .registerFilter('field-value', value => value.trim())
    // Cursomize the rendering of messages
    .on('plugins.message.displayed', function({ element, messageElement }) {
      if (element.hasAttribute(ATTR_NO_MESSAGE)) {
        messageElement.classList.add('d-none');
      } else {
        // Bootstrap's invalid-feedback is hidden unless following an `:invalid`
        // field. So we need to reveal it ourself
        messageElement.classList.add('invalid-feedback');
        messageElement.classList.add('d-block');
      }

      // Slight vocabulary tweak for checkboxes and radio
      if (element.type == 'checkbox') {
        messageElement.classList.add('invalid-feedback--checkbox');
        messageElement.textContent = messageElement.textContent.replace(
          'enter',
          'choose'
        );
      }
      if (element.type == 'radio') {
        messageElement.textContent = messageElement.textContent.replace(
          'enter',
          'choose'
        );
      }

      // Use of custom error message
      if (element.getAttribute('data-error-message')) {
        messageElement.textContent = element.getAttribute('data-error-message');
      }

      // Slight vocabulary tweak for `<select>` fields
      if (element.tagName == 'SELECT') {
        messageElement.textContent = messageElement.textContent.replace(
          'enter',
          'select'
        );
      }

      // Slight vocabulary tweak to make the message less
      // generic for some fields (for ex. date part fields)
      if (element.getAttribute('data-error-field-name')) {
        messageElement.textContent = messageElement.textContent.replace(
          new RegExp('a value', 'g'),
          element.getAttribute('data-error-field-name')
        );
      }
    });
  messagePlugin.defaultContainer.parentNode.removeChild(
    messagePlugin.defaultContainer
  );

  // Extracted from the 'Declarative' plugin
  // Expose a method for adding new elements to the validations
  // when new fields are added to the form (for ex, address fields)
  validator.addElements = function(container) {
    const opts = {};
    const prefix = declarativePlugin.opts.prefix;
    const elements = [].slice.call(
      container.querySelectorAll(`[name], [${prefix}field]`)
    );
    elements.forEach(ele => {
      const validators = declarativePlugin.parseElement(ele);
      if (!declarativePlugin.isEmptyOption(validators)) {
        const field =
          ele.getAttribute('name') || ele.getAttribute(`${prefix}field`);
        opts[field] = Object.assign({}, opts[field], validators);
      }
    });
    Object.keys(opts).forEach(field => this.addField(field, opts[field]));
  };

  return validator;
}

/**
 * Validates if the provided postcode is valid, without validating the whole form
 * @param {String} postcode The postcode to validate
 * @param {*} validator The validator to use for running the validations
 */
export function isPostcodeValid(postcode, validator) {
  const result = validator.validators.zipCode().validate({
    value: postcode.trim(),
    options: {
      country: 'GB'
    }
  });
  return result.valid;
}

/**
 * Runs given function without validating the given Element
 * @param {HTMLElement} el
 * @param {Function} fn
 */
export function withoutValidation(el, fn) {
  el.setAttribute(ATTR_NO_VALIDATION, '');
  fn();
  el.removeAttribute(ATTR_NO_VALIDATION);
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/embed/form_validation.js